import { cn, Link as UILink, type LinkProps } from "@stacklet/ui";
import { useHref } from "react-router";

type Target = "_blank" | "_self" | "_parent" | "_top";

/**
 * The one console link. Callers pick a prop and nothing else:
 *
 * - `to`   — an internal route, relative or absolute. Navigates client-side.
 * - `href` — an external URL. Opens in a new tab with an icon and safe rel.
 *
 * Target, rel, icon, and path resolution are all handled here.
 */
type Props = { children: React.ReactNode } & (
  | { to: string; href?: never; download?: never }
  | { href: string; to?: never; download?: boolean | string }
) & {
    className?: string;
    textDecoration?: "underline" | "none";
    target?: Target;
    onPress?: LinkProps["onPress"];
    truncate?: boolean;
  };

function baseClassNames(
  textDecoration: "underline" | "none",
  truncate: boolean,
  className?: string,
) {
  return cn(
    textDecoration === "none" ? "no-underline" : undefined,
    truncate
      ? "block max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
      : undefined,
    className,
  );
}

/**
 * Kept as its own component so `useHref` (which needs a Router) only runs for `to` links.
 */
function InternalLink({
  to,
  children,
  className,
  textDecoration = "underline",
  target,
  onPress,
  truncate = false,
}: { to: string } & Omit<Props, "to" | "href" | "download" | "children"> & {
    children: React.ReactNode;
  }) {
  const resolvedTo = useHref(to);

  return (
    <UILink
      className={baseClassNames(textDecoration, truncate, className)}
      href={resolvedTo}
      onPress={onPress}
      target={target}
    >
      {children}
    </UILink>
  );
}

export function Link({
  to,
  href,
  children,
  className,
  textDecoration = "underline",
  target,
  onPress,
  truncate = false,
  download,
}: Props) {
  if (import.meta.env.DEV) {
    if (to && /^(https?:|mailto:|tel:)/i.test(to)) {
      console.warn(
        `<Link to="${to}"> looks like an external URL — use the \`href\` prop for external links.`,
      );
    }
    if (href && !download && !/^(https?:|mailto:|tel:)/i.test(href)) {
      console.warn(
        `<Link href="${href}"> looks like an internal path — use the \`to\` prop for internal routes.`,
      );
    }
  }

  if (to) {
    return (
      <InternalLink
        className={className}
        onPress={onPress}
        target={target}
        textDecoration={textDecoration}
        to={to}
        truncate={truncate}
      >
        {children}
      </InternalLink>
    );
  }

  const baseClassName = baseClassNames(textDecoration, truncate, className);

  if (download) {
    return (
      <UILink
        className={baseClassName}
        download={download}
        href={href}
        onPress={onPress}
        target={target}
      >
        {children}
      </UILink>
    );
  }

  const resolvedTarget = target ?? "_blank";

  return (
    <UILink
      className={baseClassName}
      href={href}
      onPress={onPress}
      rel={resolvedTarget === "_blank" ? "noopener noreferrer" : undefined}
      target={resolvedTarget}
      external
    >
      {children}
    </UILink>
  );
}
